import type { CaptureResult } from 'posthog-js'

export type AnalyticsProperties = Record<string, string | number | boolean>

const UUID_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const NUMERIC_ID_SEGMENT_PATTERN = /^\d+$/

const SENSITIVE_PROPERTY_KEY_PATTERN =
    /(?:^|_)(?:id|ids|email|e-mail|name|first_name|last_name|firstname|lastname|phone|mobile|address|token|password|secret|amount|price|tax|gross|net|iban|bic|slug|uuid|link|client|booking|therapist|street|city|postal|zip|note|notes|message|comment|description|text|title|recipient|sender|query|search|filename|file|birthday|birthdate|diagnosis|therapy|patient|klient|invitation|otp|code)(?:$|_)/i

const URL_PROPERTY_KEYS = new Set([
    '$current_url',
    '$pathname',
    '$referrer',
    '$initial_current_url',
    '$initial_referrer',
    '$initial_pathname'
])

export const ALLOWED_ANALYTICS_PROPERTY_KEYS = new Set([
    'user_type',
    'payment_method',
    'payment_type',
    'location_type',
    'duration_bucket'
])

export function sanitizeAnalyticsUrl(url: string): string {
    try {
        const parsed = new URL(url, 'https://tebuto.local')
        const sanitizedPath = parsed.pathname
            .split('/')
            .map((segment) => {
                if (segment.length === 0) {
                    return segment
                }

                if (NUMERIC_ID_SEGMENT_PATTERN.test(segment) || UUID_SEGMENT_PATTERN.test(segment)) {
                    return ':id'
                }

                return segment
            })
            .join('/')

        return sanitizedPath.length > 0 ? sanitizedPath : '/'
    } catch {
        return '[redacted]'
    }
}

export function isSensitiveAnalyticsPropertyKey(key: string): boolean {
    if (URL_PROPERTY_KEYS.has(key)) {
        return false
    }

    return SENSITIVE_PROPERTY_KEY_PATTERN.test(key)
}

export function sanitizeAnalyticsProperties(properties: AnalyticsProperties): AnalyticsProperties {
    const sanitized: AnalyticsProperties = {}

    for (const [key, value] of Object.entries(properties)) {
        if (URL_PROPERTY_KEYS.has(key) && typeof value === 'string') {
            sanitized[key] = sanitizeAnalyticsUrl(value)
            continue
        }

        if (isSensitiveAnalyticsPropertyKey(key)) {
            continue
        }

        if (typeof value === 'string' && (value.includes('@') || UUID_SEGMENT_PATTERN.test(value))) {
            continue
        }

        sanitized[key] = value
    }

    return sanitized
}

export function pickAllowedAnalyticsProperties(
    properties: AnalyticsProperties | undefined
): AnalyticsProperties | undefined {
    if (!properties) {
        return undefined
    }

    const allowed: AnalyticsProperties = {}

    for (const [key, value] of Object.entries(properties)) {
        if (!ALLOWED_ANALYTICS_PROPERTY_KEYS.has(key)) {
            continue
        }

        allowed[key] = value
    }

    return Object.keys(allowed).length > 0 ? allowed : undefined
}

export function bucketDurationMinutes(durationMinutes: number): string {
    if (durationMinutes <= 30) {
        return 'up_to_30'
    }

    if (durationMinutes <= 60) {
        return '31_to_60'
    }

    if (durationMinutes <= 90) {
        return '61_to_90'
    }

    return 'over_90'
}

export function sanitizePostHogCaptureResult(event: CaptureResult | null): CaptureResult | null {
    if (!event) {
        return null
    }

    if (event.event === '$exception') {
        return null
    }

    return {
        ...event,
        $set: undefined,
        $set_once: undefined,
        properties: sanitizeAnalyticsProperties(event.properties as AnalyticsProperties)
    }
}
